/**
 * Trace first-party requests only.
 *
 * This was a denylist of third-party origins, and the denylist lost. Measured on
 * preview over the week to 16 September 2026, two vendors the list already named
 * under their OTHER hostnames went straight through it:
 *
 *     clerk-telemetry.com          5,640 spans   (`/\.clerk\./` misses the hyphen)
 *     forms.hscollectedforms.net   5,067 spans   (the list had `hs-analytics.net`)
 *
 * ~10.6k vendor spans in a week — at 1 span = 1 billed unit — before the feature
 * had even shipped. Meanwhile every entry that DID match was only matching
 * documentLoad `resourceFetch` spans, which never consult `ignoreUrls` at all. So
 * the list leaked the traffic it existed for and took credit for filtering it was
 * not doing. Adding the next vendor's next hostname would not have fixed the
 * shape of that.
 *
 * An allowlist needs no vendor list. The only two origins worth tracing — this
 * page and apps/api — are already known at configure time, so tomorrow's SDK is
 * ignored the day it is added rather than the day someone reads the bill. What
 * survives is `FIRST_PARTY_NOISE` below, which is a different kind of list: every
 * entry is a path we serve ourselves, so it cannot fall behind someone else's
 * release.
 *
 * The tradeoff is that it fails CLOSED: a new *Firmable* origin goes silently
 * untraced instead of noisily over-traced. That is the right way round for cost,
 * and it is mitigated by deriving the allowlist from the same options that name
 * the backend in the first place.
 *
 * Not covered here: documentLoad's per-asset `resourceFetch` spans, which no
 * `ignoreUrls` setting reaches. Those are held down by `documentLoadSampleRate`.
 */

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * An origin, matched only up to a path, query or fragment boundary.
 *
 * Without the boundary `https://api.firmable.com` also prefix-matches
 * `https://api.firmable.com.example.net` — which for `ignoreUrls` is merely
 * sloppy, but for `propagateTraceHeaderCorsUrls` would hand a `traceparent` to
 * whoever registered the lookalike.
 */
const originPrefix = (origin: string): string => `${escapeRegExp(new URL(origin).origin)}(?:[/?#]|$)`;

/** Matches exactly one origin. */
export function originPattern(origin: string): RegExp {
  return new RegExp(`^${originPrefix(origin)}`, 'i');
}

/**
 * First-party paths that are still not worth a span. This list does not drift
 * the way the vendor one did: every entry is a path WE serve, so it changes only
 * when we change, and the cost of missing one is noise rather than a blind spot.
 *
 * `/_tel/v1/` is load-bearing, not tidiness — it is the same-origin proxy the
 * exporter itself posts to, and tracing the tracer feeds itself.
 */
const FIRST_PARTY_NOISE: RegExp[] = [
  /\/_tel\/v1\//i,
  // `[/?]`, not `\/`: the optimiser is `/_next/image?url=…`, with no next segment.
  /\/_next\/(static|image|data)[/?]/i,
  // Vercel's own beacons, e.g. /_vercel/insights/view.
  /\/_vercel\//i,
  // Deployment-protection handshake, preview only — but at ~150/hour it was the
  // largest first-party source left once the vendor noise went. Scoped to
  // /vercel/ rather than all of /.well-known/, which is not ours to assume about.
  /\/\.well-known\/vercel\//i,
];

/**
 * The `ignoreUrls` value that ignores everything EXCEPT the given origins.
 *
 * OTel's fetch and XHR instrumentations match `ignoreUrls` against the resolved
 * absolute href, so an anchored negative lookahead is all an allowlist needs.
 */
export function firstPartyOnly(origins: string[]): RegExp[] {
  return [new RegExp(`^(?!${origins.map(originPrefix).join('|')})`, 'i'), ...FIRST_PARTY_NOISE];
}
